import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import {
  addPageData,
  navigateWizard,
  persistUnsupportedSchoolsInterest,
  sendBuilderState,
} from '../../actions';
import { fetchCoursesRequest } from '../../actions/fetchCoursesActions';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import SelectLoadingFetch from '../../components/SelectLoadingFetch';
import analytics from '../../lib/analytics';
import { track } from '../../lib/heapHelpers';
import useCourses from '../../lib/hooks/useCourses';
import useSchools from '../../lib/hooks/useSchools';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

const CoursePage = ({
  dispatchFetchCourses,
  dispatchAddPageData,
  dispatchWizardNav,
  dispatchUpdateUnsupportedSchoolsInterest,
  dispatchUpdateProgress,
}) => {
  const { allCourses, selectedCourse: initialCourse } = useCourses();
  const { selected: selectedSchool } = useSchools();
  const [selectedCourse, setSelectedCourse] = useState(initialCourse);
  const { REACT_APP_PRODIGY_URL: BASE_URL } = process.env;
  const [hasChanged, setHasChanged] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const { value: schoolId } = selectedSchool;
    if (!allCourses[schoolId]) {
      dispatchFetchCourses(schoolId);
    }
  }, [selectedSchool, allCourses, dispatchFetchCourses]);

  useEffect(() => {
    track('View - Course page');
  }, []);

  const isInvalid = !selectedCourse;

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'course');

    const { attendance, value: courseGuid, seasonClassGuid, underReview } = selectedCourse;
    const fullTimeCourse = attendance === 'FullTime';
    const isUnderReview = underReview === true;

    const coursePayload = payloadFactory.createAddPageDataPayload('course', selectedCourse);
    dispatchAddPageData(coursePayload);
    const courseGuidPayload = payloadFactory.createAddPageDataPayload('courseGuid', courseGuid);
    dispatchAddPageData(courseGuidPayload);

    const courseNamePayload = payloadFactory.createAddPageDataPayload(
      'courseName',
      selectedCourse.label,
    );
    dispatchAddPageData(courseNamePayload);

    dispatchAddPageData(payloadFactory.createAddPageDataPayload('periodStartDate', null));
    const partTimeCoursePayload = payloadFactory.createAddPageDataPayload(
      'partTimeCourse',
      !fullTimeCourse,
    );
    dispatchAddPageData(partTimeCoursePayload);

    const seasonClassGuidPayload = payloadFactory.createAddPageDataPayload(
      'classId',
      seasonClassGuid,
    );
    dispatchAddPageData(seasonClassGuidPayload);

    if (isUnderReview) {
      const stateOfApplicationPayload = payloadFactory.createAddPageDataPayload(
        'stateOfApplication',
        'under_review',
      );
      dispatchAddPageData(stateOfApplicationPayload);
      dispatchUpdateUnsupportedSchoolsInterest();
      dispatchUpdateProgress();
    }

    track('Submit - Course page', {
      school_name: selectedCourse.label,
      search_keyword: searchTerm,
    });
    dispatchWizardNav({ dir: 'fwd' });
  };

  if (hasChanged) {
    const clearPeriodsPayload = payloadFactory.createResetPageObjectPayLoad('periods', null);
    dispatchAddPageData(clearPeriodsPayload);
    const clearPeriodTimeStampPayload = payloadFactory.createResetPageDataPayload(
      'periodsTimeStamp',
      null,
    );
    dispatchAddPageData(clearPeriodTimeStampPayload);

    const clearCurrentLoanPeriodPayload = payloadFactory.createAddPageDataPayload(
      'currentLoanPeriod',
      {
        personalSavings: '',
        relativesContribution: '',
        amount: '',
        currency: '',
      },
      'currentLoanPeriod',
      null,
    );
    dispatchAddPageData(clearCurrentLoanPeriodPayload);

    const clearRemainderLoanPeriodPayload = payloadFactory.createAddPageDataPayload(
      'remainderLoanPeriod',
      {
        personalSavings: '',
        relativesContribution: '',
        amount: '',
        currency: '',
      },
      'remainderLoanPeriod',
      null,
    );

    dispatchAddPageData(clearRemainderLoanPeriodPayload);
  }
  const onCourseChange = course => {
    const { value: initialCourseSelected } = selectedCourse || {};
    const { value: courseSelected } = course || {};
    setHasChanged(initialCourseSelected !== courseSelected);
    setSelectedCourse(course);
  };

  const helpText =
    "We only support selected business, STEM, law, public policy and health science master's programmes.\n\n" +
    `Can't find your school? [Tell us where you want to study.](${BASE_URL}/request-a-school)`;
  return (
    <PageLayout title="What do you want to study?" helpText={helpText}>
      <form onSubmit={handleSubmit}>
        <div className="row-spacer">
          <SelectLoadingFetch
            single="course"
            plural="courses"
            onChange={onCourseChange}
            selected={selectedCourse}
            getTerm={st => setSearchTerm(st)}
            hook={useCourses}
          />
        </div>
        <div className="row-spacer">
          <Button type="submit" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

CoursePage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchFetchCourses: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchUpdateUnsupportedSchoolsInterest: PropTypes.func.isRequired,
  dispatchUpdateProgress: PropTypes.func.isRequired,
};

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchFetchCourses: fetchCoursesRequest,
  dispatchWizardNav: navigateWizard,
  dispatchUpdateUnsupportedSchoolsInterest: persistUnsupportedSchoolsInterest,
  dispatchUpdateProgress: sendBuilderState,
};

export { CoursePage };

export default connect(null, mapDispatchToProps)(CoursePage);
